import {translate} from "react-i18nify";

import * as actionTypes from "../../constants/actionTypes";
import * as componentTypes from "../../constants/componentTypes";
import * as handlers from "../../constants/handlers";
import * as methods from "../../constants/methods";
import * as responseHelper from "../../helpers/responseHelper";
import * as resultCodes from "../../constants/resultCodes";
import * as formatHelper from "../../helpers/formatHelper";
import * as messageHelper from "../../helpers/messageHelper";
import * as modelHelper from "../../helpers/modelHelper";
import * as settingsHelper from "../../helpers/settingsHelper";
import * as translationHelper from "../../helpers/translationHelper";
import * as windowService from "../windowService";

export const processWebsocketResponse = (tabId, method, response, state) => {
    switch (method) {
        case handlers.IMPORT_SETTINGS:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            return [
                messageHelper.getSuccessMessageAction("flashMessage.importSuccess"),
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_USER_SETTINGS
                    },
                    method: handlers.RELOAD_USER_SETTINGS,
                    tabId: 0
                }
            ];
        case handlers.EXPORT_SETTINGS:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            windowService.openSaveTextFileDialog(response.data, formatHelper.formatFileName("LOCAL_DATA", "dat"));

            return [];
        case handlers.EXPORT_STATIC_SQL:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            windowService.openSaveTextFileDialog(response.data, formatHelper.formatFileName("STATIC", "sql"));

            return [];
        case handlers.RESTORE_SETTINGS:
            if (response.result !== resultCodes.RESULT_OK) {
                return [
                    messageHelper.getFlashMessage(response),
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_USER_SETTINGS
                        },
                        method: handlers.RELOAD_USER_SETTINGS,
                        tabId: 0
                    }
                ];
            }

            return [messageHelper.getSuccessMessageAction("flashMessage.restoredSettings")];
        case handlers.RELOAD_USER_SETTINGS:
            if (!responseHelper.isGetUserSettingsFormatValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            let validUserSettings = settingsHelper.parseUserSettings(
                response.data,
                state.settings.options,
                state.module.productPricing,
                state.module.redlikeLightSoftware,
                state.module.gammaSoftware,
                state.rcc.machine.typeName
            );

            translationHelper.setRedlikeLocale(state.lang.languages, validUserSettings.locale);

            if (state.toDispense.activeOrder !== null) {
                return [
                    {
                        type: actionTypes.SAVE_USER_SETTINGS,
                        payload: validUserSettings
                    },
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_GET_ORDER_DETAIL,
                            params: {
                                id: state.toDispense.activeOrder.id
                            }
                        },
                        method: handlers.INTERGAMMA_MACHINE_ORDER_DETAIL_RELOAD,
                        tabId: 0
                    }
                ];
            }

            return [
                {
                    type: actionTypes.SAVE_USER_SETTINGS,
                    payload: validUserSettings
                }
            ];
        case handlers.EXPORT_LOCAL_DB:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            if (response.data) {
                return [messageHelper.getSuccessMessageAction("flashMessage.fileExportComplete")];
            }

            return [messageHelper.getErrorMessageAction("flashMessage.fileExportError")];
        case handlers.EXPORT_LOCAL_FILE:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            if (response.data) {
                return [messageHelper.getSuccessMessageAction("flashMessage.fileSaveComplete")];
            }

            return [messageHelper.getErrorMessageAction("flashMessage.fileSaveError")];
        case handlers.SUPPORTED_IMPORT_CUSTOM_DATA_FORMATS:
            if (response.result !== resultCodes.RESULT_OK) {
                return [messageHelper.getFlashMessage(response)];
            }

            return [
                {
                    type: actionTypes.CUSTOM_DATA_SAVE_SUPPORTED_IMPORT_FORMATS,
                    payload: {
                        supportedDataFormats: response.data
                    }
                }
            ];
        case handlers.IMPORT_CUSTOM_DATA:
            if (response.result !== resultCodes.RESULT_OK) {
                return [
                    messageHelper.getFlashMessage(response),
                    {
                        type: actionTypes.SET_BLOCKED_UI,
                        payload: {
                            blocked: false
                        }
                    },
                    {
                        type: actionTypes.CUSTOM_DATA_IMPORTING_END,
                        payload: {
                            imported: false,
                            errorCode: response.result
                        }
                    },
                    {
                        type: actionTypes.NAVIGATION_CLOSE_MODAL,
                        payload: {
                            type: componentTypes.IMPORT_DATA_MODAL
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.CUSTOM_DATA_IMPORTING_END,
                    payload: {
                        imported: true,
                        errorCode: response.result
                    }
                },
                {
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: false
                    }
                }
            ];
        case handlers.ABORT_CUSTOM_DATA_IMPORTING:
            if (response.result !== resultCodes.RESULT_OK) {
                return [
                    messageHelper.getFlashMessage(response),
                    {
                        type: actionTypes.CUSTOM_DATA_IMPORTING_ABORTED
                    },
                    {
                        type: actionTypes.NAVIGATION_CLOSE_MODAL,
                        payload: {
                            type: componentTypes.IMPORT_DATA_MODAL
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.CUSTOM_DATA_IMPORTING_ABORTED
                },
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.IMPORT_DATA_MODAL
                    }
                }
            ];
        case handlers.DELETE_IMPORTED_CUSTOM_DATA:
            if (response.result !== resultCodes.RESULT_OK) {
                return [
                    messageHelper.getFlashMessage(response),
                    {
                        type: actionTypes.NAVIGATION_CLOSE_MODAL,
                        payload: {
                            type: componentTypes.LOADING_MODAL
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.LOADING_MODAL
                    }
                },
                messageHelper.getSuccessMessageAction(translate("flashMessage.allRecordsDeleted"))
            ];
        case handlers.PRICING_IMPORT_DATA:
            if (response.result !== resultCodes.RESULT_OK) {
                return [
                    {
                        type: actionTypes.SET_BLOCKED_UI,
                        payload: {
                            blocked: false
                        }
                    },
                    {
                        type: actionTypes.PRICE_DATA_IMPORTING_END,
                        payload: {
                            imported: false,
                            errorCode: response.result
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.PRICE_DATA_IMPORTING_END,
                    payload: {
                        imported: response.data.map((data) => {
                            return translationHelper.getServerDialogTranslations(data);
                        }),
                        errorCode: response.result
                    }
                },
                {
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: false
                    }
                }
            ];
        case handlers.PREPARE_FILE_BACKUP_LOCAL_DB:
        case handlers.PREPARE_FILE_PRICING_DATA:
        case handlers.PREPARE_FILE_STATS_DATA:
            let preparedFileId = null;
            let fileName = null;
            let fileType = null;

            switch (method) {
                case handlers.PREPARE_FILE_PRICING_DATA:
                    preparedFileId = -1;
                    fileName = "PRICES";
                    fileType = "xlsx";
                    break;
                case handlers.PREPARE_FILE_STATS_DATA:
                    preparedFileId = -2;
                    fileName = "HISTORY";
                    fileType = "xlsx";
                    break;
                case handlers.PREPARE_FILE_BACKUP_LOCAL_DB:
                    preparedFileId = -3;
                    fileName = "REDLIKE_LOCAL_DATA_BACKUP";
                    fileType = "db";
                    break;
                default:
                    break;
            }

            if (response.result !== resultCodes.RESULT_OK) {
                return [
                    messageHelper.getFlashMessage(response),
                    {
                        type: actionTypes.NAVIGATION_CLOSE_MODAL,
                        payload: {
                            type: componentTypes.PROGRESS_BAR_MODAL
                        }
                    },
                    {
                        type: actionTypes.SERVER_DELETE_DOWNLOADED_FILE,
                        payload: {
                            downloadedFileId: preparedFileId
                        }
                    }
                ];
            }

            let downloadingFile = modelHelper.mapDownloadingFileToObject(
                response.data.id,
                fileName,
                fileType,
                response.data.length
            );

            return [
                {
                    type: actionTypes.SERVER_UPDATE_FILE,
                    payload: {
                        preparedFileId: preparedFileId,
                        downloadingFile: downloadingFile
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_PREPARE_FILE_BYTES,
                        params: {
                            id: downloadingFile.id,
                            bytesFrom: 0,
                            bytesTo: downloadingFile.downloadedLength
                        }
                    },
                    method: handlers.PREPARE_FILE_BYTES,
                    tabId: 0
                }
            ];
        case handlers.PREPARE_FILE_BYTES:
            if (!responseHelper.isPrepareFileFormatValid(response)) {
                return [
                    messageHelper.getFlashMessage(response),
                    {
                        type: actionTypes.NAVIGATION_CLOSE_MODAL,
                        payload: {
                            type: componentTypes.PROGRESS_BAR_MODAL
                        }
                    }
                ];
            }

            let preparedFile = state.server.downloadingFileList.find((item) => item.id === response.data.id);

            if (preparedFile === undefined) {
                return [
                    {
                        type: actionTypes.SERVER_PREPARE_FILE_ERROR,
                        payload: {
                            preparedFileId: response.data.id
                        }
                    }
                ];
            }

            windowService.appendFileData(preparedFile.filePath, response.data.bytes, (error) => {
                if (error === null) {
                    window.storeProvider.dispatchAction({
                        type: actionTypes.SERVER_PREPARE_FILE_SUCCESS,
                        payload: {
                            preparedFile: preparedFile
                        }
                    });
                } else {
                    windowService.logError("Download error ", error.message);

                    window.storeProvider.dispatchAction({
                        type: actionTypes.SERVER_PREPARE_FILE_ERROR,
                        payload: {
                            preparedFileId: response.data.id
                        }
                    });
                }
            });

            return [];
        case handlers.RELEASE_PREPARED_FILE:
            if (response.result !== resultCodes.RESULT_OK) {
                return [
                    messageHelper.getFlashMessage(response),
                    {
                        type: actionTypes.NAVIGATION_CLOSE_MODAL,
                        payload: {
                            type: componentTypes.PROGRESS_BAR_MODAL
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.NAVIGATION_CLOSE_MODAL,
                    payload: {
                        type: componentTypes.PROGRESS_BAR_MODAL
                    }
                }
            ];
        default:
            return [
                {
                    type: actionTypes.UNKNOWN_ACTION
                }
            ];
    }
};
